/**
 * Point Cloud Parser
 *
 * Reads the plain-text scan exchange formats the app can actually parse in
 * the browser/Tauri layer:
 * - .xyz / .pts  — whitespace or comma separated "x y z [r g b]" records
 * - .asc         — same record layout used by Civil 3D surface exports
 * - .ply (ASCII) — vertex element with optional red/green/blue properties
 *
 * Binary point cloud formats (.rcp, .rcs, .e57, .las) are proprietary/EPFL
 * container formats that need native decoders; `isParsablePointCloud` reports
 * them as unsupported so callers can show a precise message instead of an
 * empty result.
 */

export interface PointCloudData {
    /** Flat xyz triples in meters. */
    positions: Float32Array;
    /** Per-vertex rgb in 0..1, same length/stride as positions. */
    colors: Float32Array;
    /** Number of vertices. */
    count: number;
    /** Axis-aligned bounds of the set: [minX,minY,minZ,maxX,maxY,maxZ]. */
    bounds: [number, number, number, number, number, number];
    /** Source file name. */
    fileName: string;
}

const BINARY_FORMATS = ['.rcp', '.rcs', '.e57', '.las', '.laz'];
const TEXT_FORMATS = ['.xyz', '.pts', '.asc', '.ply', '.csv', '.txt'];
/** Extension of a file name, lowercased, including the dot. */
export function fileExtension(name: string): string {
    const idx = name.lastIndexOf('.');
    return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

/** True when this parser can decode the given file. */
export function isParsablePointCloud(name: string): boolean {
    const ext = fileExtension(name);
    // PLY binary variants are detected during parsing and rejected there.
    return TEXT_FORMATS.includes(ext);
}

/** True for the container formats this build cannot decode. */
export function isBinaryPointCloud(name: string): boolean {
    return BINARY_FORMATS.includes(fileExtension(name));
}

const EMPTY_BOUNDS: [number, number, number, number, number, number] =
    [0, 0, 0, 0, 0, 0];

/** Parse a scan file into a renderable point set. Throws on empty/binary. */
export async function parsePointCloudFile(file: File): Promise<PointCloudData> {
    const ext = fileExtension(file.name);

    if (isBinaryPointCloud(file.name)) {
        throw new Error(`"${file.name}" is a binary scan container (${ext}) and needs a native decoder.`);
    }

    // PLY keeps its header in ASCII even when the payload is binary; read the
    // head first so binary payloads fail with a clear message.
    if (ext === '.ply') {
        const head = await file.slice(0, 4096).text();
        if (/format\s+binary/i.test(head)) {
            throw new Error('Only ASCII PLY point clouds are supported in this build.');
        }
        return parsePly(file.name, await file.text());
    }

    return parseXyzRows(file.name, await file.text());
}

/** Parse whitespace/comma separated "x y z [r g b]" records. */
export function parseXyzRows(fileName: string, text: string): PointCloudData {
    const positions: number[] = [];
    const colors: number[] = [];
    let hasColor = false;

    for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        // Skip blanks and the comment conventions used by PTS/ASC exports.
        if (!line || line.startsWith('#') || line.startsWith('//') || line.startsWith('ply')) continue;
        const parts = line.split(/[\s,;]+/).filter(Boolean);
        if (parts.length < 3) continue;
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        const z = Number(parts[2]);
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        positions.push(x, y, z);
        if (parts.length >= 6) {
            const r = Number(parts[3]);
            const g = Number(parts[4]);
            const b = Number(parts[5]);
            if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
                // Scan exports store colors as 0..255; normalize.
                const scale = Math.max(r, g, b) > 1 ? 255 : 1;
                colors.push(r / scale, g / scale, b / scale);
                hasColor = true;
                continue;
            }
        }
        colors.push(0.75, 0.78, 0.82);
    }

    if (positions.length === 0) {
        throw new Error(`No readable points in "${fileName}".`);
    }
    return buildResult(fileName, positions, colors, hasColor);
}

/** Parse an ASCII PLY body (positions already stripped of the header). */
function parsePly(fileName: string, text: string): PointCloudData {
    const header = text;
    const props = /element\s+vertex\s+(\d+)/i.exec(header);
    const names = [...header.matchAll(/property\s+\S+\s+(\w+)/g)].map(m => m[1].toLowerCase());
    const stride = names.length || 3;
    const ix = Math.max(0, names.indexOf('x'));
    const iy = Math.max(0, names.indexOf('y'));
    const iz = Math.max(0, names.indexOf('z'));
    const ir = names.indexOf('red');
    const ig = names.indexOf('green');
    const ib = names.indexOf('blue');

    const bodyStart = header.indexOf('end_header');
    const body = bodyStart < 0 ? header : header.slice(header.indexOf('\n', bodyStart) + 1);
    const positions: number[] = [];
    const colors: number[] = [];
    const limit = props ? Number(props[1]) : Infinity;

    for (const rawLine of body.split(/\r?\n/)) {
        if (positions.length / 3 >= limit) break;
        const parts = rawLine.trim().split(/\s+/).filter(Boolean);
        if (parts.length < stride) continue;
        const x = Number(parts[ix]), y = Number(parts[iy]), z = Number(parts[iz]);
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        positions.push(x, y, z);
        if (ir >= 0 && ig >= 0 && ib >= 0) {
            const scale = 255;
            colors.push(Number(parts[ir]) / scale, Number(parts[ig]) / scale, Number(parts[ib]) / scale);
        } else {
            colors.push(0.75, 0.78, 0.82);
        }
    }

    if (positions.length === 0) {
        throw new Error(`No readable vertices in "${fileName}".`);
    }
    return buildResult(fileName, positions, colors, ir >= 0);
}

function buildResult(
    fileName: string,
    positions: number[],
    colors: number[],
    hasColor: boolean
): PointCloudData {
    const pos = Float32Array.from(positions);
    const col = Float32Array.from(colors);
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < pos.length; i += 3) {
        minX = Math.min(minX, pos[i]); maxX = Math.max(maxX, pos[i]);
        minY = Math.min(minY, pos[i + 1]); maxY = Math.max(maxY, pos[i + 1]);
        minZ = Math.min(minZ, pos[i + 2]); maxZ = Math.max(maxZ, pos[i + 2]);
    }
    if (!hasColor) {
        // Uniform neutral tone keeps the per-vertex color path valid.
        for (let i = 0; i < col.length; i += 3) {
            col[i] = 0.75; col[i + 1] = 0.78; col[i + 2] = 0.82;
        }
    }
    return {
        positions: pos,
        colors: col,
        count: pos.length / 3,
        bounds: [minX, minY, minZ, maxX, maxY, maxZ],
        fileName,
    };
}

/**
 * Andrew's monotone-chain convex hull over XY. Used by Link Topography to
 * turn a scattered ground survey point set into a closed boundary.
 */
export function convexHullXY(points: [number, number][]): [number, number][] {
    if (points.length < 3) return points.slice();
    const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cross = (o: number[], a: number[], b: number[]) =>
        (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

    const lower: number[][] = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    }
    const upper: number[][] = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return [...lower, ...upper].map(p => [p[0], p[1]] as [number, number]);
}
